import { useState, useEffect } from 'react';
import { ListChecks, FlaskConical, Check, Clock, Droplets, Beaker as BeakerIcon } from 'lucide-react';
import { useLab } from '../../../context/LabContext';
import { TestTube, Dropper } from '../../lab';
import InfoBox from '../InfoBox';

const steps = [
  { id: 1, title: 'Pipette Aliquot', short: 'Transfer extract aliquot' },
  { id: 2, title: 'Add Hydroxylamine', short: 'Reduce Fe³⁺ to Fe²⁺' },
  { id: 3, title: 'Add Acetate Buffer', short: 'Adjust pH to 3.5-4.5' },
  { id: 4, title: 'Add Phenanthroline', short: 'Form colored complex' },
  { id: 5, title: 'Wait for Color', short: 'Wait 10 minutes' },
  { id: 6, title: 'Prepare Standards', short: 'Prepare iron standards' }
];

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  2: {
    title: 'Why Reduce Fe³⁺?',
    content: 'Fe³⁺ forms a weak, blue complex with phenanthroline. Fe²⁺ forms the stable, intense orange-red [Fe(phen)3]2+ complex. Hydroxylamine reduces all iron to the +2 state.',
    chemicalEquation: '2Fe³⁺ + 2NH₂OH -> 2Fe²⁺ + N2O + H₂O + 4H+'
  },
  3: {
    title: 'pH Control',
    content: 'The iron-phenanthroline complex forms optimally at pH 3.5-4.5. Acetate buffer maintains this pH range. Outside this range, the color intensity and stability are reduced.'
  },
  4: {
    title: 'The Iron-Phenanthroline Complex',
    content: 'Three phenanthroline molecules wrap around one Fe²⁺ ion, forming a very stable octahedral complex. This "ferroin" complex is so stable that it is used as a redox indicator.',
    chemicalEquation: 'Fe²⁺ + 3(1,10-phenanthroline) -> [Fe(phen)3]2+'
  }
};

// Calculate color intensity based on concentration for iron-phenanthroline (orange-red #FF6432)
const getColorIntensity = (ppm: number, colorDeveloped: boolean): string => {
  if (!colorDeveloped) return '#D4A574'; // Brown extract color
  const maxPpm = 4.0;
  const intensity = Math.min(ppm / maxPpm, 1);
  // Interpolate from light orange to deep orange-red
  const r = 255;
  const g = Math.round(200 - intensity * 100);
  const b = Math.round(180 - intensity * 130);
  return `rgb(${r}, ${g}, ${b})`;
};

const Part2ColorDevelopment = () => {
  const { state, updateExp9Part2, completeStep, update } = useLab();
  const { exp9Part1, exp9Part2, currentStep, labMode, completedSteps } = state;

  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [dropping, setDropping] = useState<string | null>(null);
  const [showReactionModal, setShowReactionModal] = useState(false);

  // Fix: Reset currentStep to first incomplete step when this part mounts
  useEffect(() => {
    if (labMode !== 'guided') return;
    const firstIncomplete = steps.find(s => !completedSteps.includes(`colorDevelopment-${s.id}`));
    const targetStep = firstIncomplete ? firstIncomplete.id : steps[steps.length - 1].id;
    if (currentStep !== targetStep) {
      update({ currentStep: targetStep });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isActive = (s: number) => labMode === 'free' || currentStep === s;
  const isDone = (s: number) => completedSteps.includes(`colorDevelopment-${s}`);

  // Check if extraction is complete from Part 1
  const extractionComplete = exp9Part1.diluted;

  // Color development timer effect
  useEffect(() => {
    if (exp9Part2.phenanthrolineAdded && exp9Part2.colorDevelopmentProgress > 0 && exp9Part2.colorDevelopmentProgress < 100) {
      const timer = setInterval(() => {
        const newProgress = Math.min(exp9Part2.colorDevelopmentProgress + 2, 100);
        updateExp9Part2({ colorDevelopmentProgress: newProgress });

        if (newProgress >= 100) {
          updateExp9Part2({ colorDeveloped: true });
          completeStep('colorDevelopment-5');
        }
      }, 100); // Fast for demo

      return () => clearInterval(timer);
    }
  }, [exp9Part2.colorDevelopmentProgress, exp9Part2.phenanthrolineAdded]);

  // Handler: Pipette aliquot
  const handlePipetteAliquot = () => {
    if (!isActive(1) || !extractionComplete) return;
    completeStep('colorDevelopment-1');
  };

  // Handler: Add hydroxylamine
  const handleAddHydroxylamine = () => {
    if (!isActive(2) || !isDone(1)) return;
    setDropping('hydroxylamine');
    setTimeout(() => {
      setDropping(null);
      updateExp9Part2({ hydroxylamineAdded: true });
      completeStep('colorDevelopment-2');
    }, 800);
  };

  // Handler: Add buffer
  const handleAddBuffer = () => {
    if (!isActive(3) || !exp9Part2.hydroxylamineAdded) return;
    setDropping('buffer');
    setTimeout(() => {
      setDropping(null);
      updateExp9Part2({ bufferAdded: true });
      completeStep('colorDevelopment-3');
    }, 800);
  };

  // Handler: Add phenanthroline
  const handleAddPhenanthroline = () => {
    if (!isActive(4) || !exp9Part2.bufferAdded) return;
    setDropping('phenanthroline');
    setTimeout(() => {
      setDropping(null);
      updateExp9Part2({
        phenanthrolineAdded: true,
        colorDevelopmentProgress: 1
      });
      setShowReactionModal(true);
      completeStep('colorDevelopment-4');
    }, 800);
  };

  // Handler: Prepare standards
  const handlePrepareStandard = (index: number) => {
    if (!isActive(6) || !exp9Part2.colorDeveloped) return;
    const newStandards = [...exp9Part2.standards];
    newStandards[index] = { ...newStandards[index], prepared: true };
    updateExp9Part2({ standards: newStandards });

    // Check if all standards prepared
    const allPrepared = newStandards.every((s: { prepared: boolean }) => s.prepared);
    if (allPrepared) {
      completeStep('colorDevelopment-6');
    }
  };

  const completedCount = completedSteps.filter((s: string) => s.startsWith('colorDevelopment')).length;

  return (
    <div className="part2-color">
      {/* Steps Panel */}
      <div className="steps-panel">
        <div className="steps-header">
          <ListChecks size={16} />
          <h3>Part II: Color Development</h3>
        </div>
        <div className="steps-list">
          {steps.map(s => (
            <div
              key={s.id}
              className={`step-item ${isActive(s.id) && !isDone(s.id) ? 'active' : ''}`}
            >
              <div className={`step-number ${isDone(s.id) ? 'done' : isActive(s.id) ? 'current' : ''}`}>
                {isDone(s.id) ? <Check size={12} /> : s.id}
              </div>
              <div className="step-content">
                <div className="step-title-row">
                  <span className={`step-title ${isActive(s.id) ? 'highlight' : ''}`}>
                    {s.title}
                  </span>
                  {stepInfoContent[s.id] && (
                    <button
                      className="step-info-btn"
                      onClick={(e) => { e.stopPropagation(); setShowInfo(s.id); }}
                    >
                      ?
                    </button>
                  )}
                </div>
                {isActive(s.id) && !isDone(s.id) && (
                  <span className="step-hint">{s.short}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="lab-bench">
        <div className="bench-header">
          <div className="bench-title">
            <FlaskConical size={16} />
            <h3>Iron-Phenanthroline Complex Formation</h3>
          </div>
          <span className="bench-progress">{completedCount}/6 Complete</span>
        </div>

        <div className="bench-area">
          {!extractionComplete && (
            <div className="warning-message">
              Extraction not complete. Please complete Part I first.
            </div>
          )}

          {extractionComplete && (
            <>
              {/* Color Development Progress */}
              {exp9Part2.colorDevelopmentProgress > 0 && !exp9Part2.colorDeveloped && (
                <div className="development-progress">
                  <Clock size={16} />
                  <span>Color Development:</span>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${exp9Part2.colorDevelopmentProgress}%` }}
                    />
                  </div>
                  <span>{Math.round(exp9Part2.colorDevelopmentProgress)}%</span>
                </div>
              )}

              {/* Sample Flask */}
              <div className="sample-section">
                <h4>Sample Solution</h4>
                <div className="sample-flask-container">
                  <div
                    className={`sample-flask ${isActive(1) && !isDone(1) ? 'active' : ''}`}
                    onClick={handlePipetteAliquot}
                  >
                    <svg viewBox="0 0 100 140" width={100} height={140}>
                      {/* Flask body */}
                      <path
                        d="M35 15 L35 45 Q15 60 15 90 Q15 125 50 125 Q85 125 85 90 Q85 60 65 45 L65 15 Z"
                        fill="rgba(200, 220, 240, 0.3)"
                        stroke="#7A9BB8"
                        strokeWidth="2"
                      />

                      {/* Liquid fill */}
                      <path
                        d={`M17 90 Q17 122 50 122 Q83 122 83 90 Q83 65 50 65 Q17 65 17 90 Z`}
                        fill={getColorIntensity(2.5, exp9Part2.colorDeveloped)}
                        opacity="0.8"
                      />

                      {/* Neck */}
                      <rect x="35" y="5" width="30" height="12" fill="rgba(200, 220, 240, 0.4)" stroke="#7A9BB8" strokeWidth="1" />

                      {/* Reagent indicators */}
                      {exp9Part2.hydroxylamineAdded && (
                        <circle cx="30" cy="100" r="4" fill="#4CAF50" />
                      )}
                      {exp9Part2.bufferAdded && (
                        <circle cx="50" cy="100" r="4" fill="#2196F3" />
                      )}
                      {exp9Part2.phenanthrolineAdded && (
                        <circle cx="70" cy="100" r="4" fill="#FF6432" />
                      )}
                    </svg>
                    <span className="flask-label">Sample + Extract</span>
                  </div>

                  {/* Reagent status */}
                  <div className="reagent-status">
                    <div className={`status-item ${exp9Part2.hydroxylamineAdded ? 'added' : ''}`}>
                      <span className="status-dot green" />
                      Hydroxylamine {exp9Part2.hydroxylamineAdded && <Check size={12} />}
                    </div>
                    <div className={`status-item ${exp9Part2.bufferAdded ? 'added' : ''}`}>
                      <span className="status-dot blue" />
                      Buffer {exp9Part2.bufferAdded && <Check size={12} />}
                    </div>
                    <div className={`status-item ${exp9Part2.phenanthrolineAdded ? 'added' : ''}`}>
                      <span className="status-dot orange" />
                      Phenanthroline {exp9Part2.phenanthrolineAdded && <Check size={12} />}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reagent Droppers */}
              <div className="reagents-section">
                <h4>Reagents</h4>
                <div className="droppers-row">
                  <div className="dropper-container">
                    <Dropper
                      label="Hydroxylamine"
                      liquidColor="#90EE90"
                      onClick={handleAddHydroxylamine}
                      active={isActive(2) && !exp9Part2.hydroxylamineAdded && isDone(1)}
                      disabled={labMode === 'guided' && !isActive(2)}
                      dropping={dropping === 'hydroxylamine'}
                    />
                    <span className="dropper-info">Reducing agent</span>
                  </div>

                  <div className="dropper-container">
                    <Dropper
                      label="Acetate Buffer"
                      liquidColor="#87CEEB"
                      onClick={handleAddBuffer}
                      active={isActive(3) && !exp9Part2.bufferAdded && exp9Part2.hydroxylamineAdded}
                      disabled={labMode === 'guided' && !isActive(3)}
                      dropping={dropping === 'buffer'}
                    />
                    <span className="dropper-info">pH 3.5-4.5</span>
                  </div>

                  <div className="dropper-container">
                    <Dropper
                      label="1,10-Phen"
                      liquidColor="#FFD700"
                      onClick={handleAddPhenanthroline}
                      active={isActive(4) && !exp9Part2.phenanthrolineAdded && exp9Part2.bufferAdded}
                      disabled={labMode === 'guided' && !isActive(4)}
                      dropping={dropping === 'phenanthroline'}
                    />
                    <span className="dropper-info">Complexing agent</span>
                  </div>
                </div>
              </div>

              {/* Standards Preparation */}
              {exp9Part2.colorDeveloped && (
                <div className="standards-section">
                  <h4>Standard Solutions</h4>
                  <div className="standards-row">
                    {exp9Part2.standards.map((std: { ppm: number; prepared: boolean }, index: number) => (
                      <div key={index} className="tube-container">
                        <TestTube
                          fillLevel={std.prepared ? 70 : 0}
                          fillColor={getColorIntensity(std.ppm, std.prepared)}
                          label={std.ppm === 0 ? 'Blank' : `${std.ppm} ppm`}
                          onClick={() => handlePrepareStandard(index)}
                          active={isActive(6) && !std.prepared}
                          disabled={labMode === 'guided' && !isActive(6)}
                          hasResult={std.prepared}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Completion Message */}
              {isDone(6) && (
                <div className="completion-message">
                  <Check size={20} />
                  <span>Color development complete! Ready for spectrophotometric measurement.</span>
                </div>
              )}
            </>
          )}

          {/* Bench Surface */}
          <div className="bench-surface" />
        </div>
      </div>

      {/* Reaction Modal */}
      {showReactionModal && (
        <div className="modal-overlay" onClick={() => setShowReactionModal(false)}>
          <div className="modal-content reaction-modal" onClick={e => e.stopPropagation()}>
            <div className="reaction-icon">
              <BeakerIcon size={32} />
            </div>
            <h3>Complex Formation!</h3>
            <div className="reaction-diagram">
              <div className="reaction-item">
                <div className="ion fe2">Fe<sup>2+</sup></div>
                <span>+</span>
                <div className="molecule">3 Phenanthroline</div>
              </div>
              <div className="arrow">&#8594;</div>
              <div className="complex">
                <div className="complex-color" style={{ background: '#FF6432' }} />
                <span>[Fe(phen)<sub>3</sub>]<sup>2+</sup></span>
              </div>
            </div>
            <p>
              The iron-phenanthroline complex forms an intense orange-red color.
              Maximum absorbance at 510 nm.
            </p>
            <button className="modal-close-btn" onClick={() => setShowReactionModal(false)}>
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp9"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}

      <style>{`
        .part2-color {
          display: flex;
          gap: 16px;
          flex: 1;
          width: 100%;
          min-height: 0;
          padding: 16px;
        }

        .steps-panel {
          width: 240px;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .steps-header {
          background: linear-gradient(135deg, var(--primary), var(--primary-dark));
          padding: 14px 16px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
        }

        .steps-header h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .steps-list {
          flex: 1;
          overflow-y: auto;
          padding: 12px;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 8px;
          margin-bottom: 4px;
          transition: background 0.15s;
        }

        .step-item.active {
          background: rgba(255, 90, 54, 0.15);
        }

        .step-number {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 600;
          flex-shrink: 0;
          background: var(--border);
          color: var(--text-muted);
        }

        .step-number.done {
          background: var(--secondary);
          color: white;
        }

        .step-number.current {
          background: var(--primary);
          color: white;
        }

        .step-content {
          flex: 1;
          min-width: 0;
        }

        .step-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .step-title {
          font-size: 12px;
          font-weight: 600;
        }

        .step-title.highlight {
          color: var(--primary-light);
        }

        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
          border: none;
          cursor: pointer;
        }

        .step-info-btn:hover {
          transform: scale(1.1);
        }

        .step-hint {
          font-size: 11px;
          color: var(--text-secondary);
          margin-top: 4px;
          display: block;
        }

        .lab-bench {
          flex: 1;
          background: var(--bg-surface);
          border-radius: 12px;
          border: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          overflow: auto;
        }

        .bench-header {
          background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: white;
        }

        .bench-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .bench-title h3 {
          font-size: 14px;
          font-weight: 600;
        }

        .bench-progress {
          font-size: 12px;
          opacity: 0.9;
        }

        .bench-area {
          flex: 1;
          background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 24px;
          gap: 24px;
          overflow-y: auto;
        }

        .warning-message {
          background: rgba(239, 68, 68, 0.15);
          color: var(--danger);
          padding: 16px 24px;
          border-radius: 10px;
          font-size: 14px;
        }

        .development-progress {
          display: flex;
          align-items: center;
          gap: 12px;
          background: var(--bg-dark);
          padding: 12px 20px;
          border-radius: 10px;
          width: 100%;
          max-width: 500px;
        }

        .development-progress .progress-bar {
          flex: 1;
          height: 8px;
          background: var(--border);
          border-radius: 4px;
          overflow: hidden;
        }

        .development-progress .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #FF6432, #FF8050);
          transition: width 0.3s;
        }

        .development-progress span {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .sample-section, .reagents-section, .standards-section {
          text-align: center;
        }

        .sample-section h4, .reagents-section h4, .standards-section h4 {
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .sample-flask-container {
          display: flex;
          gap: 32px;
          align-items: center;
          justify-content: center;
        }

        .sample-flask {
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .sample-flask.active {
          animation: glow 1.5s ease-in-out infinite;
        }

        .flask-label {
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 8px;
        }

        .reagent-status {
          display: flex;
          flex-direction: column;
          gap: 8px;
          background: var(--bg-dark);
          padding: 12px 16px;
          border-radius: 8px;
        }

        .status-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-muted);
        }

        .status-item.added {
          color: var(--text-primary);
        }

        .status-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }

        .status-dot.green { background: #4CAF50; }
        .status-dot.blue { background: #2196F3; }
        .status-dot.orange { background: #FF6432; }

        .droppers-row {
          display: flex;
          gap: 24px;
          justify-content: center;
        }

        .dropper-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
        }

        .dropper-info {
          font-size: 10px;
          color: var(--text-muted);
        }

        .standards-row {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .tube-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(255, 90, 54, 0.3); }
          50% { box-shadow: 0 0 15px rgba(255, 90, 54, 0.5); }
        }

        .completion-message {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px;
          border-radius: 10px;
          font-size: 13px;
          background: rgba(0, 168, 107, 0.15);
          color: var(--secondary);
        }

        .bench-surface {
          display: none;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: var(--bg-surface);
          border-radius: 16px;
          padding: 24px;
          max-width: 400px;
          width: 90%;
          text-align: center;
        }

        .reaction-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #FF6432, #FF8050);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: white;
        }

        .reaction-diagram {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          background: var(--bg-dark);
          border-radius: 10px;
          padding: 20px;
          margin-bottom: 16px;
        }

        .reaction-item {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ion.fe2 {
          background: #90EE90;
          padding: 6px 12px;
          border-radius: 6px;
          font-weight: bold;
        }

        .molecule {
          font-size: 13px;
          color: var(--text-secondary);
        }

        .arrow {
          font-size: 24px;
          color: var(--text-muted);
        }

        .complex {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .complex-color {
          width: 30px;
          height: 30px;
          border-radius: 50%;
        }

        .modal-content p {
          color: var(--text-secondary);
          font-size: 13px;
          margin-bottom: 20px;
        }

        .modal-close-btn {
          padding: 12px 32px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .modal-close-btn:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};

export default Part2ColorDevelopment;
